import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User, 
  Calendar, 
  Phone, 
  Mail, 
  MapPin, 
  Heart,
  Baby,
  Activity,
  FileText,
  Download,
  Edit
} from 'lucide-react';

const PatientRecord = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  // Sample patient data - in a real app, this would be fetched from an API
  const patientData = {
    id: patientId,
    name: 'Jane Doe',
    age: 28,
    gender: 'Female',
    phone: '+234 801 234 5678',
    email: 'jane.doe@email.com',
    address: '123 Main Street, Lagos, Nigeria',
    bloodType: 'O+',
    allergies: ['Penicillin', 'Peanuts'],
    lastVisit: '2025-12-15',
    nextAppointment: '2026-03-15',
    pregnancyStatus: 'Third Trimester (32 weeks)',
    dueDate: '2025-10-25',
    vitals: {
      weight: '68 kg',
      height: '165 cm',
      bloodPressure: '120/80 mmHg',
      heartRate: '72 bpm',
      temperature: '36.8°C'
    },
    medicalHistory: [
      {
        id: 1,
        date: '2024-03-20',
        type: 'Prenatal Checkup',
        diagnosis: 'Normal pregnancy progression',
        notes: 'All vitals normal. Continue prenatal vitamins. Next visit in 4 weeks.',
        doctor: 'Dr. Sarah Johnson'
      },
      {
        id: 2,
        date: '2024-02-15',
        type: 'Blood Test',
        diagnosis: 'Mild iron deficiency',
        notes: 'Iron supplement prescribed. Recheck in 1 month.',
        doctor: 'Dr. Michael Chen'
      },
      {
        id: 3,
        date: '2024-01-10',
        type: 'Ultrasound',
        diagnosis: 'Healthy fetal development',
        notes: 'Fetal growth on track. No complications detected.',
        doctor: 'Dr. Sarah Johnson'
      }
    ],
    prescriptions: [
      {
        id: 1,
        medication: 'Prenatal Multivitamin',
        dosage: '1 tablet daily',
        prescribedDate: '2024-01-10',
        status: 'Active'
      },
      {
        id: 2,
        medication: 'Iron Supplement',
        dosage: '65mg once daily',
        prescribedDate: '2024-02-15',
        status: 'Active'
      }
    ],
    labResults: [
      {
        id: 1,
        test: 'Complete Blood Count',
        date: '2024-02-15',
        result: 'Mild anemia',
        file: 'cbc-results.pdf'
      },
      {
        id: 2,
        test: 'Glucose Test',
        date: '2024-01-10',
        result: 'Normal',
        file: 'glucose-test.pdf'
      }
    ],
    childHealthRecords: [
      {
        id: 1,
        childName: 'Emma Doe',
        age: '3 years',
        lastVisit: '2024-03-10',
        vaccinations: 'Up to date',
        notes: 'Healthy development'
      }
    ]
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/doctor/patients')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Patients
        </button>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-10 h-10 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{patientData.name}</h1>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {patientData.age} years old
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {patientData.phone}
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {patientData.email}
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Blood Type: {patientData.bloodType}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'overview'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('medical-history')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'medical-history'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Medical History
        </button>
        <button
          onClick={() => setActiveTab('prescriptions')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'prescriptions'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Prescriptions
        </button>
        <button
          onClick={() => setActiveTab('lab-results')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'lab-results'
              ? 'text-emerald-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Lab Results
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Current Vitals */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-600" />
              Current Vitals
            </h2>
            <div className="space-y-3">
              {Object.entries(patientData.vitals).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                  <span className="text-gray-600 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                  <span className="font-semibold text-gray-800">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Pregnancy Status */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-emerald-600" />
              Pregnancy Status
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Status</span>
                <span className="font-semibold text-gray-800">{patientData.pregnancyStatus}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Due Date</span>
                <span className="font-semibold text-gray-800">{new Date(patientData.dueDate).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-gray-600">Last Visit</span>
                <span className="font-semibold text-gray-800">{new Date(patientData.lastVisit).toLocaleDateString()}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Next Appointment</span>
                <span className="font-semibold text-emerald-600">{new Date(patientData.nextAppointment).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Allergies */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4">Allergies</h2>
            <div className="flex flex-wrap gap-2">
              {patientData.allergies.map((allergy, index) => (
                <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {allergy}
                </span>
              ))}
            </div>
          </div>

          {/* Child Health */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Baby className="w-5 h-5 text-emerald-600" />
              Child Health Records
            </h2>
            {patientData.childHealthRecords.map(child => (
              <div key={child.id} className="space-y-2">
                <p className="font-semibold text-gray-800">{child.childName}</p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Age: {child.age}</p>
                  <p>Last Visit: {new Date(child.lastVisit).toLocaleDateString()}</p>
                  <p>Vaccinations: {child.vaccinations}</p>
                  <p>Notes: {child.notes}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Medical History Tab */}
      {activeTab === 'medical-history' && (
        <div className="space-y-4">
          {patientData.medicalHistory.map(record => (
            <div key={record.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{record.type}</h3>
                  <p className="text-sm text-gray-600">{new Date(record.date).toLocaleDateString()} • {record.doctor}</p>
                </div>
                <button className="text-emerald-600 hover:text-emerald-700">
                  <Edit className="w-4 h-4" />
                </button>
              </div>
              <div className="space-y-2">
                <div>
                  <span className="text-sm font-medium text-gray-700">Diagnosis:</span>
                  <p className="text-gray-800">{record.diagnosis}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Notes:</span>
                  <p className="text-gray-800">{record.notes}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Prescriptions Tab */}
      {activeTab === 'prescriptions' && (
        <div className="grid md:grid-cols-2 gap-4">
          {patientData.prescriptions.map(prescription => (
            <div key={prescription.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-800">{prescription.medication}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  prescription.status === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {prescription.status}
                </span>
              </div>
              <div className="space-y-2 text-sm text-gray-600">
                <p><span className="font-medium">Dosage:</span> {prescription.dosage}</p>
                <p><span className="font-medium">Prescribed:</span> {new Date(prescription.prescribedDate).toLocaleDateString()}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lab Results Tab */}
      {activeTab === 'lab-results' && (
        <div className="space-y-4">
          {patientData.labResults.map(result => (
            <div key={result.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <FileText className="w-8 h-8 text-emerald-600" />
                  <div>
                    <h3 className="font-semibold text-gray-800">{result.test}</h3>
                    <p className="text-sm text-gray-600">{new Date(result.date).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-gray-700">Result: {result.result}</span>
                  <button className="flex items-center gap-2 px-3 py-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors">
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PatientRecord;
